/**
 * Menu Blobo - Animated character for menu screens
 *
 * Features idle animations: breathing, blinking, gentle bounce
 */

import Phaser from 'phaser';

export class MenuBlobo extends Phaser.GameObjects.Container {
  private bloboBody: Phaser.GameObjects.Graphics;
  private leftEye: Phaser.GameObjects.Container;
  private rightEye: Phaser.GameObjects.Container;
  private leftPupil: Phaser.GameObjects.Graphics;
  private rightPupil: Phaser.GameObjects.Graphics;
  private isBlinking: boolean = false;
  private size: number;

  constructor(scene: Phaser.Scene, x: number, y: number, size: number = 80) {
    super(scene, x, y);
    this.size = size;

    // Create Blobo body
    this.bloboBody = scene.add.graphics();
    this.drawBody();
    this.add(this.bloboBody);

    // Create eyes - spacing matches game (0.45 * radius)
    this.leftEye = this.createEye(-size * 0.45, -size * 0.15);
    this.rightEye = this.createEye(size * 0.45, -size * 0.15);
    this.add([this.leftEye, this.rightEye]);

    // Store pupil references
    this.leftPupil = this.leftEye.getAt(1) as Phaser.GameObjects.Graphics;
    this.rightPupil = this.rightEye.getAt(1) as Phaser.GameObjects.Graphics;

    // Start animations
    this.startIdleAnimations();

    // Add to scene display list
    scene.add.existing(this);
  }

  /**
   * Draw Blobo's body (white circle with outline)
   */
  private drawBody(): void {
    const radius = this.size;

    // Main body
    this.bloboBody.fillStyle(0xffffff, 1);
    this.bloboBody.lineStyle(3, 0x000000, 0.3);
    this.bloboBody.fillCircle(0, 0, radius);
    this.bloboBody.strokeCircle(0, 0, radius);

    // Subtle gradient effect (using multiple circles)
    this.bloboBody.fillStyle(0xffffff, 0.3);
    this.bloboBody.fillCircle(-radius * 0.2, -radius * 0.2, radius * 0.6);
  }

  /**
   * Create an eye with white background and black pupil
   * Matches game Blobo proportions exactly (radius * 0.5)
   */
  private createEye(x: number, y: number): Phaser.GameObjects.Container {
    const eyeRadius = this.size * 0.5; // Match game: radius * 0.5 (was 0.2)
    const pupilRadius = eyeRadius * 0.5;

    const eyeContainer = this.scene.add.container(x, y);

    // White of eye
    const white = this.scene.add.graphics();
    white.fillStyle(0xffffff, 1);
    white.lineStyle(2, 0x000000, 0.3);
    white.fillCircle(0, 0, eyeRadius);
    white.strokeCircle(0, 0, eyeRadius);

    // Pupil
    const pupil = this.scene.add.graphics();
    pupil.fillStyle(0x000000, 1);
    pupil.fillCircle(0, 0, pupilRadius);

    eyeContainer.add([white, pupil]);

    return eyeContainer;
  }

  /**
   * Start idle animations
   */
  private startIdleAnimations(): void {
    // Breathing animation (gentle scale)
    this.scene.tweens.add({
      targets: this,
      scaleY: { from: 1.0, to: 1.05 },
      scaleX: { from: 1.0, to: 0.98 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Gentle bounce
    this.scene.tweens.add({
      targets: this,
      y: this.y - 10,
      duration: 3000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Random blinking
    this.scheduleNextBlink();
  }

  /**
   * Schedule next blink (random interval)
   */
  private scheduleNextBlink(): void {
    const delay = Phaser.Math.Between(2000, 5000); // 2-5 seconds

    this.scene.time.delayedCall(delay, () => {
      this.blink();
    });
  }

  /**
   * Perform blink animation
   */
  private blink(): void {
    if (this.isBlinking) return;
    this.isBlinking = true;

    // Squash eyes vertically to simulate blink
    this.scene.tweens.add({
      targets: [this.leftEye, this.rightEye],
      scaleY: 0.1,
      duration: 100,
      yoyo: true,
      onComplete: () => {
        this.isBlinking = false;
        this.scheduleNextBlink();
      },
    });
  }

  /**
   * Make pupils look in a direction
   */
  lookAt(targetX: number, targetY: number): void {
    const worldPos = this.getWorldTransformMatrix();
    const dx = targetX - worldPos.tx;
    const dy = targetY - worldPos.ty;
    const angle = Math.atan2(dy, dx);
    const eyeRadius = this.size * 0.5; // Match game proportions
    const maxOffset = eyeRadius * 0.4; // Match game: eyeRadius * 0.4

    const offsetX = Math.cos(angle) * maxOffset;
    const offsetY = Math.sin(angle) * maxOffset;

    // Animate pupils
    this.scene.tweens.add({
      targets: this.leftPupil,
      x: offsetX,
      y: offsetY,
      duration: 200,
      ease: 'Quad.easeOut',
    });

    this.scene.tweens.add({
      targets: this.rightPupil,
      x: offsetX,
      y: offsetY,
      duration: 200,
      ease: 'Quad.easeOut',
    });
  }

  /**
   * Update pupils based on tilt angle (googly eyes effect)
   * @param tiltAngle Tilt angle in degrees (±15)
   */
  updatePupilsFromTilt(tiltAngle: number): void {
    const eyeRadius = this.size * 0.5;
    const maxOffset = eyeRadius * 0.4; // Match game movement range

    // Normalize tilt to -1 to 1 range (±15° range)
    const normalizedTilt = Phaser.Math.Clamp(tiltAngle / 15, -1, 1);

    // Move pupils horizontally based on tilt
    const offsetX = normalizedTilt * maxOffset;

    // Smooth transition
    this.scene.tweens.add({
      targets: [this.leftPupil, this.rightPupil],
      x: offsetX,
      duration: 150,
      ease: 'Quad.easeOut',
    });
  }

  /**
   * Reset pupils to center
   */
  lookCenter(): void {
    this.scene.tweens.add({
      targets: [this.leftPupil, this.rightPupil],
      x: 0,
      y: 0,
      duration: 300,
      ease: 'Quad.easeOut',
    });
  }

  /**
   * Jump animation (excited)
   */
  jump(): void {
    this.scene.tweens.add({
      targets: this,
      y: this.y - 30,
      scaleY: 1.2,
      scaleX: 0.9,
      duration: 300,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
  }
}
